/**
 * Pre-defined Filter Patterns for Sensitive Data Detection
 * 
 * Comprehensive collection of regex patterns for detecting common sensitive data types.
 */

import { FilterPattern, SensitiveDataCategory } from './filter-types';

/**
 * Social Security Number patterns (US format)
 */
export const SSN_PATTERNS: FilterPattern[] = [
  {
    id: 'ssn-standard',
    name: 'Social Security Number (Standard)',
    description: 'Detects US Social Security Numbers in XXX-XX-XXXX format',
    category: SensitiveDataCategory.PII,
    regex: /\b(?!000|666|9\d{2})\d{3}[-\s]?(?!00)\d{2}[-\s]?(?!0000)\d{4}\b/g,
    testCases: [
      '123-45-6789',
      '123 45 6789',
      '123456789'
    ],
    falsePositives: [
      '000-00-0000',
      '666-00-0000',
      '900-00-0000'
    ]
  }
];

/**
 * Credit Card Number patterns
 */
export const CREDIT_CARD_PATTERNS: FilterPattern[] = [
  {
    id: 'cc-visa',
    name: 'Visa Credit Card',
    description: 'Detects Visa credit card numbers (16 digits starting with 4)',
    category: SensitiveDataCategory.FINANCIAL,
    regex: /\b4[0-9]{12}(?:[0-9]{3})?\b/g,
    testCases: [
      '4111111111111111',
      '4012888888881881',
      '4222222222222'
    ]
  },
  {
    id: 'cc-mastercard',
    name: 'MasterCard Credit Card',
    description: 'Detects MasterCard credit card numbers',
    category: SensitiveDataCategory.FINANCIAL,
    regex: /\b5[1-5][0-9]{14}\b/g,
    testCases: [
      '5555555555554444',
      '5105105105105100'
    ]
  },
  {
    id: 'cc-amex',
    name: 'American Express Credit Card',
    description: 'Detects American Express credit card numbers (15 digits)',
    category: SensitiveDataCategory.FINANCIAL,
    regex: /\b3[47][0-9]{13}\b/g,
    testCases: [
      '378282246310005',
      '371449635398431'
    ]
  },
  {
    id: 'cc-discover',
    name: 'Discover Credit Card',
    description: 'Detects Discover credit card numbers',
    category: SensitiveDataCategory.FINANCIAL,
    regex: /\b6(?:011|5[0-9]{2})[0-9]{12}\b/g,
    testCases: [
      '6011111111111117',
      '6511111111111117'
    ]
  },
  {
    id: 'cc-generic',
    name: 'Generic Credit Card',
    description: 'Detects generic credit card patterns with separators',
    category: SensitiveDataCategory.FINANCIAL,
    regex: /\b(?:\d{4}[-\s]?){3}\d{4}\b|\b(?:\d{4}[-\s]?){2}\d{7}\b/g,
    testCases: [
      '1234-5678-9012-3456',
      '1234 5678 9012 3456',
      '1234567890123456'
    ]
  }
];

/**
 * Email Address patterns
 */
export const EMAIL_PATTERNS: FilterPattern[] = [
  {
    id: 'email-standard',
    name: 'Email Address',
    description: 'Detects standard email addresses',
    category: SensitiveDataCategory.PII,
    regex: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    testCases: [
      'user@example.com',
      'test.email+tag@domain.co.uk',
      'firstname.lastname@company.org'
    ],
    falsePositives: [
      'test@test',
      '@example.com',
      'user@'
    ]
  }
];

/**
 * Phone Number patterns
 */
export const PHONE_PATTERNS: FilterPattern[] = [
  {
    id: 'phone-us',
    name: 'US Phone Number',
    description: 'Detects US phone numbers in various formats',
    category: SensitiveDataCategory.PII,
    regex: /\b(?:\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})\b/g,
    testCases: [
      '(555) 123-4567',
      '+1-555-123-4567',
      '555.123.4567',
      '5551234567'
    ]
  },
  {
    id: 'phone-international',
    name: 'International Phone Number',
    description: 'Detects international phone numbers with country codes',
    category: SensitiveDataCategory.PII,
    regex: /\+(?:[0-9] ?){6,14}[0-9]/g,
    testCases: [
      '+44 20 7946 0958',
      '+33 1 42 86 83 26',
      '+81 3 1234 5678'
    ]
  }
];

/**
 * IP Address patterns
 */
export const IP_PATTERNS: FilterPattern[] = [
  {
    id: 'ipv4',
    name: 'IPv4 Address',
    description: 'Detects IPv4 addresses',
    category: SensitiveDataCategory.PII,
    regex: /\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\b/g,
    testCases: [
      '192.168.1.1',
      '10.0.0.1',
      '172.16.0.1',
      '8.8.8.8'
    ],
    falsePositives: [
      '999.999.999.999',
      '256.256.256.256'
    ]
  },
  {
    id: 'ipv6',
    name: 'IPv6 Address',
    description: 'Detects IPv6 addresses',
    category: SensitiveDataCategory.PII,
    regex: /\b(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}\b|\b::1\b|\b::\b/g,
    testCases: [
      '2001:0db8:85a3:0000:0000:8a2e:0370:7334',
      '2001:db8:85a3::8a2e:370:7334',
      '::1',
      '::'
    ]
  }
];

/**
 * API Key and Token patterns
 */
export const API_KEY_PATTERNS: FilterPattern[] = [
  {
    id: 'aws-access-key',
    name: 'AWS Access Key',
    description: 'Detects AWS Access Key IDs',
    category: SensitiveDataCategory.AUTHENTICATION,
    regex: /AKIA[0-9A-Z]{16}/g,
    testCases: [
      'AKIAIOSFODNN7EXAMPLE'
    ]
  },
  {
    id: 'aws-secret-key',
    name: 'AWS Secret Key',
    description: 'Detects AWS Secret Access Keys',
    category: SensitiveDataCategory.AUTHENTICATION,
    regex: /[A-Za-z0-9/+=]{40}/g,
    testCases: [
      'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY'
    ]
  },
  {
    id: 'google-api-key',
    name: 'Google API Key',
    description: 'Detects Google API keys',
    category: SensitiveDataCategory.AUTHENTICATION,
    regex: /AIza[0-9A-Za-z-_]{35}/g,
    testCases: [
      'AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI'
    ]
  },
  {
    id: 'stripe-key',
    name: 'Stripe API Key',
    description: 'Detects Stripe API keys',
    category: SensitiveDataCategory.AUTHENTICATION,
    regex: /sk_live_[0-9a-zA-Z]{24}|pk_live_[0-9a-zA-Z]{24}/g,
    testCases: [
      'sk_live_1234567890abcdef12345678',
      'pk_live_1234567890abcdef12345678'
    ]
  },
  {
    id: 'jwt-token',
    name: 'JWT Token',
    description: 'Detects JSON Web Tokens',
    category: SensitiveDataCategory.AUTHENTICATION,
    regex: /eyJ[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g,
    testCases: [
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c'
    ]
  },
  {
    id: 'github-token',
    name: 'GitHub Token',
    description: 'Detects GitHub personal access tokens',
    category: SensitiveDataCategory.AUTHENTICATION,
    regex: /ghp_[A-Za-z0-9_]{36}|gho_[A-Za-z0-9_]{36}|ghu_[A-Za-z0-9_]{36}|ghs_[A-Za-z0-9_]{36}|ghr_[A-Za-z0-9_]{36}/g,
    testCases: [
      'ghp_1234567890abcdef1234567890abcdef12345678',
      'gho_1234567890abcdef1234567890abcdef12345678'
    ]
  },
  {
    id: 'openai-api-key',
    name: 'OpenAI API Key',
    description: 'Detects OpenAI API keys',
    category: SensitiveDataCategory.AUTHENTICATION,
    regex: /sk-[A-Za-z0-9]{16,48}/g,
    testCases: [
      'sk-1234567890abcdef1234567890abcdef1234567890abcdef'
    ]
  }
];

/**
 * Password and Authentication patterns
 */
export const PASSWORD_PATTERNS: FilterPattern[] = [
  {
    id: 'password-field',
    name: 'Password Field',
    description: 'Detects password-related field names and values',
    category: SensitiveDataCategory.AUTHENTICATION,
    regex: /(?:password|passwd|pwd|pass)[\s]*[:=][\s]*[^\s\n\r]+/gi,
    testCases: [
      'password: mypassword123',
      'passwd=secretpass',
      'pwd : hidden123'
    ]
  }
];

/**
 * Bank Account and Financial patterns
 */
export const BANK_PATTERNS: FilterPattern[] = [
  {
    id: 'iban',
    name: 'IBAN Number',
    description: 'Detects International Bank Account Numbers',
    category: SensitiveDataCategory.FINANCIAL,
    regex: /\b[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}\b/g,
    testCases: [
      'GB82WEST12345698765432',
      'DE89370400440532013000',
      'FR1420041010050500013M02606'
    ]
  },
  {
    id: 'routing-number',
    name: 'US Bank Routing Number',
    description: 'Detects US bank routing numbers in appropriate contexts',
    category: SensitiveDataCategory.FINANCIAL,
    regex: /\b(?:routing|routing_number|aba|bank_code)[\s]*[:=]?[\s]*[0-9]{9}\b/gi,
    testCases: [
      'routing: 021000021',
      'routing_number=011401533',
      'ABA 121000248'
    ]
  },
  {
    id: 'cvv',
    name: 'CVV/CVC Code',
    description: 'Detects credit card CVV/CVC security codes in appropriate contexts',
    category: SensitiveDataCategory.FINANCIAL,
    regex: /\b(?:cvv|cvc|security_code|card_code)[\s]*[:=]?[\s]*[0-9]{3,4}\b/gi,
    testCases: [
      'cvv: 123',
      'CVC=4567',
      'security_code 789'
    ]
  }
];

/**
 * Medical/Health patterns
 */
export const MEDICAL_PATTERNS: FilterPattern[] = [
  {
    id: 'medical-record',
    name: 'Medical Record Number',
    description: 'Detects medical record numbers',
    category: SensitiveDataCategory.MEDICAL,
    regex: /\bMRN[\s]*[:=]?[\s]*[A-Z0-9-]{5,15}\b/gi,
    testCases: [
      'MRN: 12345-6789',
      'MRN=ABC123DEF456',
      'mrn 987654321'
    ]
  }
];

/**
 * Common keyword patterns for various sensitive data types
 */
export const KEYWORD_PATTERNS = {
  // Authentication related
  authentication: [
    'password', 'passwd', 'pwd', 'pass', 'secret', 'key', 'token', 'auth', 'authorization',
    'credential', 'cred', 'apikey', 'api_key', 'access_token', 'refresh_token', 'bearer',
    'oauth', 'jwt', 'session', 'cookie', 'csrf', 'xsrf'
  ],
  
  // Personal information
  pii: [
    'ssn', 'social_security', 'social', 'ein', 'tax_id', 'driver_license', 'passport',
    'credit_card', 'debit_card', 'account_number', 'routing_number', 'iban',
    'phone', 'email', 'address', 'birth_date', 'birthday', 'dob'
  ],
  
  // Financial
  financial: [
    'account', 'balance', 'transaction', 'payment', 'billing', 'invoice',
    'salary', 'income', 'revenue', 'profit', 'loss', 'tax', 'irs'
  ],
  
  // Medical
  medical: [
    'patient', 'medical', 'health', 'diagnosis', 'treatment', 'prescription',
    'medicine', 'drug', 'symptom', 'allergy', 'mrn', 'medical_record'
  ]
};

/**
 * Get all pre-defined patterns grouped by category
 */
export function getAllPredefinedPatterns(): Record<SensitiveDataCategory, FilterPattern[]> {
  return {
    [SensitiveDataCategory.PII]: [
      ...SSN_PATTERNS,
      ...EMAIL_PATTERNS,
      ...PHONE_PATTERNS,
      ...IP_PATTERNS
    ],
    [SensitiveDataCategory.AUTHENTICATION]: [
      ...API_KEY_PATTERNS,
      ...PASSWORD_PATTERNS
    ],
    [SensitiveDataCategory.FINANCIAL]: [
      ...CREDIT_CARD_PATTERNS,
      ...BANK_PATTERNS
    ],
    [SensitiveDataCategory.MEDICAL]: [
      ...MEDICAL_PATTERNS
    ],
    [SensitiveDataCategory.CUSTOM]: []
  };
}

/**
 * Get all pre-defined patterns as a flat array
 */
export function getAllPredefinedPatternsFlat(): FilterPattern[] {
  const groupedPatterns = getAllPredefinedPatterns();
  return Object.values(groupedPatterns).flat();
}

/**
 * Get patterns by category
 */
export function getPatternsByCategory(category: SensitiveDataCategory): FilterPattern[] {
  const allPatterns = getAllPredefinedPatterns();
  return allPatterns[category] || [];
}

/**
 * Get pattern by ID
 */
export function getPatternById(id: string): FilterPattern | undefined {
  const allPatterns = getAllPredefinedPatterns();
  for (const patterns of Object.values(allPatterns)) {
    const pattern = patterns.find(p => p.id === id);
    if (pattern) return pattern;
  }
  return undefined;
}

/**
 * Validate a pattern against its test cases
 */
export function validatePattern(pattern: FilterPattern): { 
  valid: boolean; 
  results: { testCase: string; matched: boolean }[];
  falsePositives: { case: string; matched: boolean }[];
} {
  const results = pattern.testCases.map(testCase => ({
    testCase,
    matched: pattern.regex.test(testCase)
  }));
  
  const falsePositives = (pattern.falsePositives || []).map(falseCase => ({
    case: falseCase,
    matched: pattern.regex.test(falseCase)
  }));
  
  const allTestsPassed = results.every(r => r.matched);
  const noFalsePositives = falsePositives.every(fp => !fp.matched);
  
  return {
    valid: allTestsPassed && noFalsePositives,
    results,
    falsePositives
  };
}

/**
 * Test all pre-defined patterns
 */
export function validateAllPatterns(): Record<string, { valid: boolean; error?: string }> {
  const allPatterns = getAllPredefinedPatterns();
  const results: Record<string, { valid: boolean; error?: string }> = {};
  
  for (const patterns of Object.values(allPatterns)) {
    for (const pattern of patterns) {
      try {
        const validation = validatePattern(pattern);
        results[pattern.id] = { valid: validation.valid };
        if (!validation.valid) {
          results[pattern.id].error = 'Pattern validation failed';
        }
      } catch (error) {
        results[pattern.id] = { 
          valid: false, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        };
      }
    }
  }
  
  return results;
} 
